<template>
  <v-form :disabled="state.loading || formSubmitted">
    <br />

    <v-col cols="12" :sm="state.forceCols || 6" class="pa-0">
      <div>
        <v-btn text block large color="edit" class="mb-5" @click="clearForm">
          <v-icon left>$iconReset</v-icon>
          {{
            $t(
              "wallet:billingContacts.manageContact.clearForm.button",
              "Clear form"
            )
          }}
        </v-btn>
      </div>

      <AppSelectContact
        v-if="
          state.v$.form.basedOnContactId &&
            state.personContacts &&
            state.personContacts.length
        "
        :based-on-contact-id.sync="stateForm.basedOnContactId"
        :contacts="state.personContacts"
        :input-props="{
          hideDetails: true
        }"
        class="entsys--white-background-input mb-2"
        @selected-contact="
          selectedContact => $emit('set-form', selectedContact)
        "
      />

      <v-checkbox
        v-model="contactType"
        :label="
          $t(
            'wallet:billingContacts.manageContact.toOrganisation.checkbox.label',
            'I would like an invoice or receipt issued to an organisation or company'
          )
        "
        :disabled="
          state.fieldDisabled('contactType') || !!stateForm.basedOnContactId
        "
        data-test="contactTypeCheckbox"
        true-value="ORGANISATION"
        false-value="PERSON"
        class="mb-2"
      />

      <v-row
        :no-gutters="$vuetify.breakpoint.xsOnly"
        :dense="$vuetify.breakpoint.smAndDown"
      >
        <v-col cols="12" sm="6">
          <v-text-field
            v-if="state.v$.form.firstName"
            v-model="stateForm.firstName"
            :label="
              $t(
                'wallet:billingContacts.manageContact.firstName.label',
                'First name'
              )
            "
            data-test="firstName"
            class="entsys--white-background-input"
            :disabled="state.fieldDisabled('firstName')"
            :error-messages="state.validationErrors('firstName')"
            outlined
            @blur="state.v$.form.firstName.$touch()"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-if="state.v$.form.lastName"
            v-model="stateForm.lastName"
            :label="
              $t(
                'wallet:billingContacts.manageContact.lastName.label',
                'Last name'
              )
            "
            class="entsys--white-background-input"
            data-test="lastName"
            :disabled="state.fieldDisabled('lastName')"
            :error-messages="state.validationErrors('lastName')"
            outlined
            @blur="state.v$.form.lastName.$touch()"
          />
        </v-col>
      </v-row>

      <AppSelectCountries
        v-if="state.v$.form.registryCountry"
        v-model="stateForm.registryCountry"
        :label="
          $t(
            'wallet:billingContacts.manageContact.countryOfRegistration.label',
            'Country of registration'
          )
        "
        :hint="
          $t(
            'wallet:billingContacts.manageContact.countryOfRegistration.hint',
            'Select a country, where organization or company is registered'
          )
        "
        persistent-hint
        class="entsys--white-background-input"
        data-test="registryCountry"
        :input-props="{
          disabled: state.fieldDisabled('registryCountry'),
          errorMessages: state.validationErrors('registryCountry'),
          returnObject: true
        }"
        @change="
          registryCountry =>
            (stateForm.registryCountryId = registryCountry
              ? registryCountry.id
              : null)
        "
        @blur="state.v$.form.registryCountry.$touch()"
      />

      <v-text-field
        v-if="state.v$.form.organisationName"
        v-model="stateForm.organisationName"
        :label="
          $t(
            'wallet:billingContacts.manageContact.nameOfOrganisation.label',
            'Name of organisation'
          )
        "
        class="entsys--white-background-input"
        :disabled="state.fieldDisabled('organisationName')"
        :error-messages="state.validationErrors('organisationName')"
        data-test="organisationName"
        outlined
        @blur="state.v$.form.organisationName.$touch()"
      />

      <v-text-field
        v-if="state.v$.form.vatCode"
        v-model="stateForm.vatCode"
        :label="
          $t(
            'wallet:billingContacts.manageContact.VATNumber.label',
            'VAT number or tax ID'
          )
        "
        :hint="
          $t(
            'wallet:billingContacts.manageContact.VATNumber.hint',
            'This field is optional. Add VAT number or tax ID if it should be quoted on financial document'
          )
        "
        persistent-hint
        class="entsys--white-background-input"
        :disabled="state.fieldDisabled('vatCode')"
        :error-messages="state.validationErrors('vatCode')"
        data-test="vatCode"
        outlined
        @blur="state.v$.form.vatCode.$touch()"
      />

      <AppSelectLegalForm
        v-if="state.v$.form.legalFormId"
        v-model="stateForm.legalFormId"
        :label="
          $t(
            'wallet:billingContacts.manageContact.legalForm.label',
            'Legal form'
          )
        "
        class="entsys--white-background-input"
        :disabled="state.fieldDisabled('legalFormId')"
        :error-messages="state.validationErrors('legalFormId')"
        data-test="legalFormId"
        @blur="state.v$.form.legalFormId.$touch()"
      />

      <v-text-field
        v-if="state.v$.form.addressText"
        v-model="stateForm.addressText"
        :disabled="state.fieldDisabled('addressText')"
        :error-messages="state.validationErrors('addressText')"
        class="entsys--white-background-input"
        :label="
          $t(
            'wallet:billingContacts.manageContact.streetAddress.label',
            'Street address'
          )
        "
        outlined
        data-test="addressText"
        @blur="state.v$.form.addressText.$touch()"
      />
      <v-row :no-gutters="$vuetify.breakpoint.xsOnly">
        <v-col cols="12" sm="6">
          <v-text-field
            v-if="state.v$.form.addressHouse"
            v-model="stateForm.addressHouse"
            :disabled="state.fieldDisabled('addressHouse')"
            :error-messages="state.validationErrors('addressHouse')"
            class="entsys--white-background-input"
            :label="
              $t(
                'wallet:billingContacts.manageContact.buildingNr.label',
                'Building nr'
              )
            "
            outlined
            data-test="addressHouse"
            @blur="state.v$.form.addressHouse.$touch()"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-if="state.v$.form.addressBox"
            v-model="stateForm.addressBox"
            :disabled="state.fieldDisabled('addressBox')"
            :error-messages="state.validationErrors('addressBox')"
            class="entsys--white-background-input"
            :label="
              $t('wallet:billingContacts.manageContact.addressBox.label', 'Box')
            "
            outlined
            data-test="addressBox"
            @blur="state.v$.form.addressHouse.$touch()"
          />
        </v-col>
      </v-row>
      <v-text-field
        v-if="state.v$.form.addressCity"
        v-model="stateForm.addressCity"
        :disabled="state.fieldDisabled('addressCity')"
        :error-messages="state.validationErrors('addressCity')"
        class="entsys--white-background-input"
        :label="
          $t(
            'wallet:billingContacts.manageContact.city.label',
            'City or municipality'
          )
        "
        outlined
        data-test="addressCity"
        @blur="state.v$.form.addressCity.$touch()"
      />

      <v-text-field
        v-if="state.v$.form.addressState"
        v-model="stateForm.addressState"
        :disabled="state.fieldDisabled('addressState')"
        :error-messages="state.validationErrors('addressState')"
        class="entsys--white-background-input"
        :label="
          $t(
            'wallet:billingContacts.manageContact.provinceOrRegion.label',
            'Region or province'
          )
        "
        outlined
        data-test="addressState"
        @blur="state.v$.form.addressState.$touch()"
      />

      <v-text-field
        v-if="state.v$.form.addressIndex"
        v-model="stateForm.addressIndex"
        :disabled="state.fieldDisabled('addressIndex')"
        :error-messages="state.validationErrors('addressIndex')"
        class="entsys--white-background-input"
        :label="
          $t(
            'wallet:billingContacts.manageContact.postalCode.label',
            'Postal code'
          )
        "
        outlined
        data-test="addressIndex"
        @blur="state.v$.form.addressIndex.$touch()"
      />

      <AppSelectCountries
        v-if="state.v$.form.addressCountry"
        v-model="stateForm.addressCountry"
        :label="
          $t('wallet:billingContacts.manageContact.country.label', 'Country')
        "
        class="entsys--white-background-input"
        data-test="addressCountry"
        :input-props="{
          disabled: state.fieldDisabled('addressCountry'),
          errorMessages: state.validationErrors('addressCountry'),
          returnObject: true
        }"
        @change="
          addressCountry =>
            (stateForm.addressCountryId = addressCountry
              ? addressCountry.id
              : null)
        "
        @blur="state.v$.form.addressCountry.$touch()"
      />

      <v-text-field
        v-if="state.v$.form.billingEmail"
        v-model="stateForm.billingEmail"
        :disabled="state.fieldDisabled('billingEmail')"
        :error-messages="state.validationErrors('billingEmail')"
        class="entsys--white-background-input"
        :label="
          $t(
            'wallet:billingContacts.manageContact.emailForBilling.label',
            'Billing e-mail'
          )
        "
        :hint="
          $t(
            'wallet:billingContacts.manageContact.emailForBilling.hint',
            'E-mail will be used to receive billing messages'
          )
        "
        hint-persistent
        outlined
        data-test="billingEmail"
        @blur="state.v$.form.billingEmail.$touch()"
      />

      <v-checkbox
        v-if="state.v$.form.storeAsPersonContact"
        v-model="saveToContacts"
        :disabled="state.fieldDisabled('storeAsPersonContact')"
        :label="
          $t(
            'wallet:billingContacts.manageContact.saveToContacts.label',
            'Save information to my contact profile'
          )
        "
        :hint="
          $t(
            'wallet:billingContacts.manageContact.saveToContacts.hint',
            'When checked, changes will be saved under existing contact profile or a new profile will be created'
          )
        "
      />

      <v-checkbox
        v-if="state.v$.form.defaultForBilling"
        v-model="saveAsDefaultBilling"
        :disabled="state.fieldDisabled('defaultForBilling')"
        :label="
          $t(
            'wallet:billingContacts.manageContact.setAsDefault.label',
            'Set contact as primary contact for billing'
          )
        "
        :hint="
          $t(
            'wallet:billingContacts.manageContact.setAsDefault.hint',
            'When checked, this contact information will be offered as default, when billing contacts are asked'
          )
        "
      />

      <AppBtnsRow>
        <template #left="{attributes}">
          <v-btn v-bind="attributes" @click="$emit('close-edit-form')">
            {{ $t("common:button.cancel", "Cancel", "Cancel button") }}
          </v-btn></template
        >
        <template #right="{attributes}">
          <v-btn
            data-test="contactSubmit"
            v-bind="attributes"
            color="accent"
            :loading="formSubmitted"
            :disabled="state.v$.form.$invalid"
            @click="submitContact"
            >{{ $t("common:button.save", "Save", "Save button") }}</v-btn
          ></template
        >
      </AppBtnsRow>
    </v-col>
  </v-form>
</template>
<script>
import { defineComponent, ref, computed, getCurrentInstance } from "vue";
import gql from "fraql";
import { apolloClient } from "@/plugins/apollo";
import AppSelectCountries from "@AppSelects/AppSelectCountries.vue";
import AppBtnsRow from "@elements/globals/AppBtnsRow.vue";
import AppSelectContact from "@AppSelects/AppSelectContact.vue";
import AppSelectLegalForm from "@AppSelects/AppSelectLegalForm.vue";
import ContactFieldFragment from "./fragment";

export default defineComponent({
  name: "AppBillingContactForm",
  components: {
    AppSelectCountries,
    AppBtnsRow,
    AppSelectContact,
    AppSelectLegalForm
  },
  props: {
    form: {
      type: Object,
      required: true
    },
    state: {
      type: Object,
      required: true
    }
  },
  emits: ["update:form", "set-contact", "set-form", "submitted-contact"],
  setup(props, ctx) {
    const instance = getCurrentInstance();

    const saveToContacts = ref(false);
    const saveAsDefaultBilling = ref(false);
    const formSubmitted = ref(false);

    const contactType = computed({
      get() {
        return props.form.contactType;
      },
      set(v) {
        stateForm.value.contactType = v;
      }
    });

    const stateForm = computed({
      get() {
        return props.form;
      },
      set(v) {
        ctx.emit("update:form", v);
      }
    });

    async function submitContact() {
      if (
        saveToContacts.value == false &&
        saveAsDefaultBilling.value == false
      ) {
        ctx.emit("set-contact", stateForm.value);
        ctx.emit("submitted-contact", stateForm.value); // in InvoiceDetails, this will trigger mutation to save buyerContact to be associated to invoice
        return;
      }

      formSubmitted.value = true;

      try {
        switch (contactType.value) {
          case "PERSON":
            await personContactSave();
            break;
          case "ORGANISATION":
            await organisationContactSave();
            break;
        }

        ctx.emit("set-contact", stateForm.value);
        ctx.emit("submitted-contact", stateForm.value); // in InvoiceDetails, this will trigger mutation to save buyerContact to be associated to invoice
      } catch (error) {
        instance.proxy.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        formSubmitted.value = false;
      }
    }

    function personContactSave() {
      const f = stateForm.value;
      return apolloClient.mutate({
        mutation: gql`
          mutation personContactPersonCreateOrUpdate(
            $id: ID
            $personId: ID!
            $firstName: String
            $lastName: String
            $defaultForBilling: Boolean
            $registryCountryId: ID
            $addressText: String
            $addressHouse: String
            $addressIndex: String
            $addressCity: String
            $addressBox: String
            $addressState: String
            $addressCountryId: ID
            $billingEmail: String
          ) {
            personContactPersonCreateOrUpdate(
              input: {
                id: $id
                personId: $personId
                firstName: $firstName
                lastName: $lastName
                defaultForBilling: $defaultForBilling
                registryCountryId: $registryCountryId
                addressText: $addressText
                addressHouse: $addressHouse
                addressIndex: $addressIndex
                addressCity: $addressCity
                addressBox: $addressBox
                addressState: $addressState
                addressCountryId: $addressCountryId
                billingEmail: $billingEmail
              }
            ) {
              contact {
                id
                personId
                firstName
                lastName
                defaultForBilling
                registryCountryId
                addressText
                addressHouse
                addressBox
                addressIndex
                addressCity
                addressState
                addressCountryId
                billingEmail
              }
            }
          }
        `,
        variables: {
          id: f.id,
          personId: f.personId,
          firstName: f.firstName,
          lastName: f.lastName,
          defaultForBilling: saveAsDefaultBilling.value,
          registryCountryId: f.registryCountryId,
          addressText: f.addressText,
          addressHouse: f.addressHouse,
          addressBox: f.addressBox,
          addressIndex: f.addressIndex,
          addressCity: f.addressCity,
          addressState: f.addressState,
          addressCountryId: f.addressCountryId,
          billingEmail: f.billingEmail
        }
      });
    }

    function organisationContactSave() {
      const f = stateForm.value;
      return apolloClient.mutate({
        mutation: gql`
          mutation personContactOrganisationCreateOrUpdate(
            $id: ID
            $personId: ID!
            $organisationName: String
            $vatCode: String
            $legalFormId: ID
            $defaultForBilling: Boolean
            $registryCountryId: ID
            $addressText: String
            $addressHouse: String
            $addressIndex: String
            $addressBox: String
            $addressCity: String
            $addressState: String
            $addressCountryId: ID
            $billingEmail: String
          ) {
            personContactOrganisationCreateOrUpdate(
              input: {
                id: $id
                personId: $personId
                organisationName: $organisationName
                vatCode: $vatCode
                legalFormId: $legalFormId
                defaultForBilling: $defaultForBilling
                registryCountryId: $registryCountryId
                addressText: $addressText
                addressHouse: $addressHouse
                addressBox: $addressBox
                addressIndex: $addressIndex
                addressCity: $addressCity
                addressState: $addressState
                addressCountryId: $addressCountryId
                billingEmail: $billingEmail
              }
            ) {
              contact {
                id
                personId
                organisationName
                vatCode
                legalFormId
                defaultForBilling
                registryCountryId
                addressText
                addressHouse
                addressIndex
                addressCity
                addressState
                addressBox
                addressCountryId
                billingEmail
              }
            }
          }
        `,
        variables: {
          id: f.id,
          personId: f.personId,
          organisationName: f.organisationName,
          vatCode: f.vatCode,
          legalFormId: f.legalFormId,
          description: f.description,
          defaultForBilling: saveAsDefaultBilling.value,
          registryCountryId: f.registryCountryId,
          addressText: f.addressText,
          addressHouse: f.addressHouse,
          addressIndex: f.addressIndex,
          addressCity: f.addressCity,
          addressState: f.addressState,
          addressBox: f.addressBox,
          addressCountryId: f.addressCountryId,
          billingEmail: f.billingEmail
        }
      });
    }

    function clearForm() {
      saveToContacts.value = false;
      saveAsDefaultBilling.value = false;
      ctx.emit("set-form", null);
    }

    return {
      stateForm,

      saveToContacts,
      saveAsDefaultBilling,

      contactType,

      submitContact,
      formSubmitted,
      clearForm
    };
  },
  fragments: {
    contactFields: gql`
      fragment _  on Contact {
        id
        ${ContactFieldFragment.contactFields}
        registryCountryId
        registryCountry @client {
          id
          ${AppSelectCountries.fragments.countryFields}
        }

        addressCountryId
        addressCountry @client {
          id
          ${AppSelectCountries.fragments.countryFields}
        }
      }
    `
  }
});
</script>
